import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Provider } from "@ethersproject/abstract-provider";

import { useWeb3Context } from "./useWeb3Context";

import axios from "axios";

import {
  BlockPolledLiquityStore,
  EthersLiquity,
  EthersLiquityWithStore,
  _connectByChainId,
} from "@yeti/lib-ethers";

type LiquityContextValue = {
  account: string;
  provider: Provider;
  liquity: EthersLiquityWithStore<BlockPolledLiquityStore>;
};

const LiquityContext = createContext<LiquityContextValue | undefined>(
  undefined
);

type LiquityProviderProps = {
  loader?: React.ReactNode;
  unsupportedNetworkFallback?: (chainId: number) => React.ReactNode;
  unsupportedMainnetFallback?: React.ReactNode;
};

const supportedNetworks = ["fuji", "avalanche"];

export const LiquityProvider: React.FC<LiquityProviderProps> = ({
  children,
  loader,
  unsupportedNetworkFallback,
  unsupportedMainnetFallback,
}) => {
  const { provider, connected, account, chainId, signer } = useWeb3Context();

  const [userCountry, setUserCountry] = useState<string>("");

  useEffect(() => {
    const getGeoInfo = () => {
      axios
        .get("https://ipapi.co/json/")
        .then((response) => {
          const data = response.data;
          setUserCountry(data.country_name);
        })
        .catch((error) => {
          console.log(error);
        });
    };
    getGeoInfo();
  }, []);

  const [isBlockedCountry, setIsBlockedCountry] = useState<boolean>(false);

  useEffect(() => {
    setIsBlockedCountry(
      account !== "0xca9fff2f9838d326e4e8b4c68fedc653f60fe1b5" &&
        userCountry === "United States"
    );
  }, [userCountry]);

  const connection = useMemo(() => {
    if (provider && account && chainId) {
      try {
        return _connectByChainId(provider, signer, chainId, {
          userAddress: account,
          useStore: "blockPolled",
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, [provider, account, chainId]);

  // if (config.testnetOnly && chainId === 1) {
  //   return <>{unsupportedMainnetFallback}</>;
  // }

  if (!connection) {
    return unsupportedNetworkFallback ? (
      <>{unsupportedNetworkFallback(chainId)}</>
    ) : null;
  }

  const liquity = EthersLiquity._from(connection);
  liquity.store.logging = true;
  console.log("liquity", liquity);
  return (
    <LiquityContext.Provider value={{ account, provider, liquity }}>
      {children}
    </LiquityContext.Provider>
  );
};

export const useLiquity = () => {
  const liquityContext = useContext(LiquityContext);

  if (!liquityContext) {
    throw new Error("You must provide a LiquityContext via LiquityProvider");
  }

  return liquityContext;
};
